import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, User, Building2, Users } from "lucide-react";

const plans = [
  {
    name: "Customer",
    icon: User,
    price: "Free",
    description: "For wellness seekers",
    features: [
      "Search & discover providers",
      "Book appointments online",
      "Booking history",
      "Purchases history",
      "Reviews & ratings",
    ],
    cta: "Get Started Free",
    variant: "outline" as const,
    popular: false,
  },
  {
    name: "Solo Practitioner",
    icon: Users,
    price: "Free",
    description: "For individual wellness professionals",
    features: [
      "Business profile page",
      "Service listings",
      "Online scheduling",
      "Point of Sale",
      "Customer management",
      "Product inventory",
    ],
    cta: "Get Started Free",
    variant: "hero" as const,
    popular: true,
  },
  {
    name: "Premium Business",
    icon: Building2,
    price: "$9.99",
    period: "/month",
    description: "For wellness centers with 2+ practitioners",
    features: ["Everything in Solo", "Multi-practitioner support", "Team management", "Advanced reports", "Analytics"],
    cta: "Start 30-Day Trial",
    variant: "outline" as const,
    popular: false,
  },
];

export function PricingPreview() {
  return (
    <section className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Choose the plan that fits your wellness business</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={plan.name}
              variant={plan.popular ? "gradient" : "elevated"}
              className={`relative p-6 animate-fade-up ${
                plan.popular ? "border-primary shadow-wellness-glow scale-105" : ""
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="gradient-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${
                    plan.popular ? "gradient-primary" : "bg-primary/10"
                  }`}
                >
                  <plan.icon className={`w-7 h-7 ${plan.popular ? "text-primary-foreground" : "text-primary"}`} />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-display text-4xl font-bold text-foreground">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground">{plan.period}</span>}
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button variant={plan.variant} className="w-full" asChild>
                <Link to="/register">
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/pricing" className="text-primary font-medium hover:underline">
            View full pricing details →
          </Link>
        </div>
      </div>
    </section>
  );
}
